import { on } from 'delegated-events';
import abort from '../../../javascripts/utils/abort';
import onLoad from '../../../javascripts/utils/onLoad';

const wordsCount = (value: string) => value.split(/\S+/).length - 1;

const charsCount = (value: string) => [...value]
  .map((char) => {
    const codePoint = char.codePointAt(0);
    if (codePoint && codePoint > 127) {
      return `&#${codePoint};`;
    }

    return char;
  })
  .join('').length;

const updateCount = ($input: HTMLInputElement | HTMLTextAreaElement) => {
  const {
    inputLimit: limit,
    inputLimitType: limitType = 'characters',
    inputLimitCount: limitCount = false,
  } = $input.dataset;

  if (!limit || !limitCount) {
    return;
  }

  requestIdleCallback(() => {
    const $limit = document.getElementById(limit) ?? abort();
    const $limitDisplay = $limit.querySelector('strong') ?? abort();
    const value = $input.isContentEditable ? $input.innerHTML : $input.value;
    const count = limitType === 'words' ? wordsCount(value) : charsCount(value);
    const left = parseInt(limitCount, 10) - count;

    // Update display
    $limitDisplay.innerText = left.toString();
  });
};

const useInputLimits = ($input: HTMLInputElement | HTMLTextAreaElement) => {
  // Update on load
  updateCount($input);

  // Update on keydown, change and paste
  const eventListener = () => updateCount($input);
  $input.addEventListener('keydown', eventListener);
  $input.addEventListener('change', eventListener);
  $input.addEventListener('paste', eventListener);

  // Deregister function
  return () => {
    $input.removeEventListener('keydown', eventListener);
    $input.removeEventListener('change', eventListener);
    $input.removeEventListener('paste', eventListener);
  };
};

on('click', '.input', (event) => {
  const { currentTarget: $container } = event;

  if (event.target instanceof Element) {
    if (event.target.closest('.input__input') || event.target.closest('.input__picker')) {
      return;
    }
  }

  const $input = $container.querySelector<HTMLElement>('.input__input');

  $input?.focus({
    preventScroll: true,
  });

  if ($input instanceof HTMLSelectElement) {
    $input.showPicker();
  }
});

on('click', '.input__picker', (event) => {
  const { currentTarget: $picker } = event;

  if ('showPicker' in HTMLInputElement.prototype) {
    try {
      const $input = $picker.parentElement?.querySelector<HTMLInputElement>('input.input__input');

      $input?.focus({ preventScroll: true });
      $input?.showPicker();
    } catch {
      // Do nothing.
    }
  }
});

on('click', 'input.input__input[data-input-select-on-click]', (event) => {
  const { currentTarget: $input } = event;

  $input.select();
  $input.setSelectionRange(0, $input.value.length);
});

on('change', 'input.input__input[type^=date], input.input__input[type^=time]', (event) => {
  const { currentTarget: $input } = event;
  $input.toggleAttribute('data-input-empty', $input.value === '');
});

onLoad<HTMLInputElement>('input.input__input[type^=date], input.input__input[type^=time]', ($input) => {
  $input.toggleAttribute('data-input-empty', $input.value === '');
});

onLoad<HTMLInputElement | HTMLTextAreaElement>('.input__input[data-input-limit]', ($input) => {
  useInputLimits($input);
});
